import React from 'react'
import { gql, useQuery, useMutation } from '@apollo/client'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Formik, Form, Field, FieldProps } from 'formik'
import { Row, Col, Select, Radio, Checkbox } from 'antd'
import { LegalStatusType, DomiciliationType } from '../../../types/index'
import CentredLoader from '../../../components/CentredLoader'
import {
  openValidationPopup,
  openErrorPopup,
} from '../../../components/ConfirmationPopups'
import { button } from '../../../styles/button'
import MailOptions from './MailOptions'
import Addresses from './Addresses'

const PROSPECT_INFO = gql`
  query getProspectInfo($id: Int!, $countryConfigId: ID!) {
    legalStatuses(countryConfigId: $countryConfigId) {
      id
      name
    }
    countries {
      code
      name
    }
    domiciliation(id: $id) {
      id
      whitelistAllPaymentTypes
      forwardingAddress {
        country
      }
      address {
        id
      }
      society {
        id
        actionType
        legalStatus {
          id
          name
        }
      }
      mailService {
        id
      }
    }
  }
`

interface Props {
  domiciliation: DomiciliationType
  refetchDomiciliation: any
  setEstimationVariables: any
  estimationVariables: any
  readOnlyForm: boolean
}

const PROSPECT_UPDATE = gql`
  mutation prospectUpdate(
    $domiciliationId: Int!
    $society: SocietyInput
    $addressId: Int
    $mailServiceId: Int
    $forwardingCountry: String
    $whitelistAllPaymentTypes: Boolean!
  ) {
    prospectUpdate(
      domiciliationId: $domiciliationId
      society: $society
      addressId: $addressId
      mailServiceId: $mailServiceId
      forwardingCountry: $forwardingCountry
      whitelistAllPaymentTypes: $whitelistAllPaymentTypes
    ) {
      domiciliation {
        id
      }
    }
  }
`

const Infos = (props: Props): JSX.Element => {
  const { t } = useTranslation()
  const { countryConfig } = props.domiciliation
  const { loading, data } = useQuery(PROSPECT_INFO, {
    fetchPolicy: 'cache-first',
    variables: {
      id: props.domiciliation.id,
      countryConfigId: countryConfig!.id,
    },
  })

  const [prospectUpdate, { loading: updateProspectLoading }] =
    useMutation(PROSPECT_UPDATE)

  if (loading) return <CentredLoader />
  const { domiciliation, legalStatuses, countries } = data

  const actionTypes = ['CREATE', 'TRANSFERT']

  const { Option } = Select

  const radioStyle = {
    display: 'block',
    height: '30px',
    lineHeight: '30px',
    fontSize: '14px',
  }

  return (
    <Wrapper>
      <Formik
        initialValues={{
          whitelistAllPaymentTypes: domiciliation.whitelistAllPaymentTypes,
          society: {
            id: domiciliation.society.id,
            actionType: domiciliation.society.actionType,
            legalStatusId: domiciliation.society?.legalStatus?.id,
          },
          addressId: domiciliation.address?.id,
          mailServiceId: domiciliation.mailService?.id,
          forwardingCountry: domiciliation.forwardingAddress?.country,
        }}
        onSubmit={values => {
          prospectUpdate({
            variables: { domiciliationId: domiciliation.id, ...values },
          })
            .then(() => props.refetchDomiciliation())
            .then(() =>
              openValidationPopup(
                t('updateProspect.successTitle'),
                t('updateProspect.successDescription')
              )
            )
            .catch(e =>
              openErrorPopup(t('updateProspect.errorTitle'), e.message)
            )
        }}
      >
        <Form>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <h4>Société</h4>
              <Row>
                <Col span={12}>
                  <Row>
                    <label>{t('society.actionType.label')}</label>
                  </Row>
                  <Row>
                    <Field name="society.actionType">
                      {({ field, form }: FieldProps) => (
                        <Radio.Group
                          onChange={e => {
                            form.setFieldValue(field.name, e.target.value)
                            props.setEstimationVariables({
                              ...props.estimationVariables,
                              actionType: e.target.value,
                            })
                          }}
                          defaultValue={field.value}
                        >
                          {actionTypes.map((item: string) => {
                            return (
                              <Radio
                                key={item}
                                value={item}
                                style={radioStyle}
                                disabled={props.readOnlyForm}
                              >
                                {t(`society.actionType.${item}`)}
                              </Radio>
                            )
                          })}
                        </Radio.Group>
                      )}
                    </Field>
                  </Row>
                </Col>
                <Col span={12}>
                  <Row>
                    <label>{t('legalStatus.label')}</label>
                  </Row>
                  <Row>
                    <Field name="society.legalStatusId">
                      {({ field, form }: FieldProps) => (
                        <Select
                          disabled={props.readOnlyForm}
                          showSearch
                          defaultValue={field.value}
                          onChange={e => {
                            form.setFieldValue(field.name, e)
                            props.setEstimationVariables({
                              ...props.estimationVariables,
                              legalStatusId: e,
                            })
                          }}
                          filterOption={(input, option) =>
                            (option!.children as unknown as string)
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          style={{ width: '250px' }}
                        >
                          {legalStatuses.map((item: LegalStatusType) => {
                            return (
                              <Option key={item.id} value={item.id}>
                                {item.name}
                              </Option>
                            )
                          })}
                        </Select>
                      )}
                    </Field>
                  </Row>
                </Col>
              </Row>
            </Col>

            <Col span={12}>
              <h4>Domiciliation & Courrier</h4>
              <Row>
                <Col span={12}>
                  <Field name="addressId">
                    {({ field, form }: FieldProps) => (
                      <Addresses
                        field={field}
                        form={form}
                        countryConfig={countryConfig!}
                        setEstimationVariables={props.setEstimationVariables}
                        estimationVariables={props.estimationVariables}
                        readOnlyForm={props.readOnlyForm}
                      />
                    )}
                  </Field>
                </Col>
                <Col span={12}>
                  <Field name="mailServiceId">
                    {({ field, form }: FieldProps) => (
                      <MailOptions
                        countryConfigId={countryConfig?.id}
                        field={field}
                        form={form}
                        setEstimationVariables={props.setEstimationVariables}
                        estimationVariables={props.estimationVariables}
                        readOnlyForm={props.readOnlyForm}
                      />
                    )}
                  </Field>
                  <Row>
                    <label>{t('forwardingCountry.label')}</label>
                  </Row>
                  <Row>
                    <Field name="forwardingCountry">
                      {({ field, form }: FieldProps) => (
                        <Select
                          disabled={props.readOnlyForm}
                          showSearch
                          defaultValue={field.value}
                          onChange={e => {
                            form.setFieldValue(field.name, e)
                            props.setEstimationVariables({
                              ...props.estimationVariables,
                              forwardingCountry: e,
                            })
                          }}
                          filterOption={(input, option) =>
                            (option!.children as unknown as string)
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          style={{ width: '250px' }}
                        >
                          {countries.map((item: any) => {
                            return (
                              <Option key={item.code} value={item.code}>
                                {item.name}
                              </Option>
                            )
                          })}
                        </Select>
                      )}
                    </Field>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <h4>Options</h4>
              <Row>
                <Field name="whitelistAllPaymentTypes">
                  {({ field, form }: FieldProps) => (
                    <Checkbox
                      checked={field.value}
                      onChange={e => {
                        form.setFieldValue(field.name, e.target.checked)
                      }}
                    >
                      Autoriser tous les moyens de paiement
                    </Checkbox>
                  )}
                </Field>
              </Row>
            </Col>
          </Row>
          <Row justify="end">
            <Col span={4}>
              {updateProspectLoading ? (
                <CentredLoader />
              ) : (
                <button
                  type="submit"
                  disabled={updateProspectLoading || props.readOnlyForm}
                >
                  ENREGISTRER
                </button>
              )}
            </Col>
          </Row>
        </Form>
      </Formik>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  margin-left: 3%;
  margin-right: 3%;

  label {
    padding-left: 3px;
    display: block;
    font-size: 12px;
  }

  ${button}
`

export default Infos
